import React from 'react'

import { reactular } from '@vendhq/reactular'

const CSS_CLASSES = {
  NEGATIVE: 'wr-number-colour-negative',
  NEUTRAL: 'wr-number-colour-neutral',
  POSITIVE: 'wr-number-colour-positive',
}

/**
 * Get the appropriate CSS class for the given number to determine its colour.
 *
 * @param  number
 *         The number to get the CSS class for
 *
 * @param  polariseNumberFn
 *         An optional function to determine the number's sign.
 *
 * @return The CSS class to apply that will appropriately colour the number
 */
function getNumberColourClass(
  n: number | string,
  polariseNumberFn?: (n: number | string) => number
) {
  let parsedNumber: number
  if (polariseNumberFn) {
    parsedNumber = polariseNumberFn(n)
  } else if (typeof n === 'string') {
    parsedNumber = parseFloat(n)
  } else {
    parsedNumber = n
  }

  if (isNaN(parsedNumber) || parsedNumber === 0) {
    return CSS_CLASSES.NEUTRAL
  } else if (parsedNumber > 0) {
    return CSS_CLASSES.POSITIVE
  } else {
    return CSS_CLASSES.NEGATIVE
  }
}

interface Props {
  number: number | string
  polariseNumberFn?: (n: number | string) => number
}

/**
 * Coloured display of a number based on whether it is negative. Negative is red, positive is green, anything else
 * (0, undefined, NaN, etc) is black.
 *
 * @param props.number The number to colour.
 *
 * @param props.polariseNumberFn
 *        Optional function to polarise the number (determine whether it is positive/negative/neutral) before
 *        determining its colour.
 *
 * @return The rendered component content.
 */
export const ColouredNumber: React.FC<Props> = ({
  number,
  polariseNumberFn,
}) => {
  const className = getNumberColourClass(number, polariseNumberFn)
  return <span className={className}>{number.toString()}</span>
}

export const wrColouredNumber = reactular<Props>(ColouredNumber, [
  'number',
  'polariseNumberFn',
])
